// iter 403 — Platform click-usage tracker. Records ONLY a normalised
// action id (data-testid with record ids stripped), the page route and a
// count. Never input values, PINs, customer data or free text.
import BACKEND_URL from "./apiConfig";

const ID_PARTS = /(rec[a-z0-9]{14}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{24}|\d{4}-\d{2}-\d{2})/gi;
const NUM_SEG = /(^|[^a-z0-9])\d{3,}(?![a-z0-9])/g;
const ID_SEG = /^(rec[a-z0-9]{14}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{24}|\d+)$/i;
const CLICKABLE =
  "button, a[href], summary, select, input[type=checkbox], input[type=radio], input[type=submit], input[type=button], " +
  "[role=button], [role=menuitem], [role=menuitemcheckbox], [role=menuitemradio], [role=tab], [role=option], " +
  "[role=checkbox], [role=switch], [role=link], [role=radio], [role=combobox], label[for]";
const TEXT_TAGS = new Set(["BUTTON", "A", "SUMMARY"]);
const FLUSH_MS = 10000;
const SWEEP_MS = 5000;
const MAX_QUEUE = 400;

export function normalizeId(raw) {
  return String(raw || "")
    .toLowerCase()
    .replace(/[^\x20-\x7e]/g, "")
    .replace(ID_PARTS, "*")
    .replace(NUM_SEG, "$1*")
    .replace(/\*(?:[-_.]\*)+/g, "*")
    .slice(0, 120)
    .trim();
}

export function normalizeRoute(pathname = "", search = "") {
  const segs = pathname.split("/").map((s) => (ID_SEG.test(s) || s.length >= 20 ? ":id" : s));
  const tab = new URLSearchParams(search).get("tab");
  return (segs.join("/") || "/") + (tab ? `?tab=${tab.slice(0, 30)}` : "");
}

function hasReactClick(el) {
  const key = Object.keys(el).find((k) => k.startsWith("__reactProps"));
  const p = key ? el[key] : null;
  return !!(p && (p.onClick || p.onPointerDown || p.onMouseDown));
}

export function isClickable(el) {
  return !!el && el.nodeType === 1 && (el.matches(CLICKABLE) || hasReactClick(el));
}

export function resolveClickable(target) {
  let el = target;
  for (let i = 0; el && el !== document.body && i < 12; i++) {
    if (isClickable(el)) return el;
    el = el.parentElement;
  }
  return null;
}

function slug(text) {
  const t = String(text || "").trim().replace(/\s+/g, " ");
  if (!t || t.length > 32 || /\d{3,}|@/.test(t)) return "";
  const words = t.split(" ");
  if (words.length >= 2 && words.every((w) => /^[A-Z][a-z'.-]*$/.test(w))) return "name";
  return t.toLowerCase().replace(/[^a-z0-9 ]/g, "").trim().replace(/ /g, "-");
}

export function actionIdFor(el) {
  const own = el.getAttribute("data-testid");
  if (own) return normalizeId(own);
  const ctx = el.parentElement?.closest("[data-testid]")?.getAttribute("data-testid");
  const tag = el.tagName.toLowerCase();
  const label = slug(el.getAttribute("aria-label")) || (TEXT_TAGS.has(el.tagName) ? slug(el.textContent) : "") ||
    slug(el.getAttribute("title")) || (el.getAttribute("role") || "");
  return normalizeId(`${ctx ? normalizeId(ctx) + ">" : ""}${tag}${label ? ":" + label : ""}`);
}

function isVisible(el) {
  return typeof el.checkVisibility === "function" ? el.checkVisibility() : el.getClientRects().length > 0;
}

export function startUsageTracker() {
  if (typeof window === "undefined") return () => {};
  const queue = new Map();
  let seenDay = "";
  let seen = new Set();
  let sweepTimer = null;

  const route = () => normalizeRoute(window.location.pathname, window.location.search);
  const enqueue = (t, a, r) => {
    if (!a || queue.size >= MAX_QUEUE) return;
    const k = `${t}|${a}|${r}`;
    queue.set(k, (queue.get(k) || 0) + 1);
  };

  const flush = (keepalive = false) => {
    const token = localStorage.getItem("session_token");
    if (!queue.size || !token) return;
    const events = [...queue.entries()].map(([k, n]) => {
      const [t, a, r] = k.split("|");
      return { t, a, r, n };
    });
    queue.clear();
    fetch(`${BACKEND_URL}/api/usage/events`, {
      method: "POST",
      keepalive,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ events, origin: window.location.origin }),
    }).catch(() => {});
  };

  const sweep = () => {
    if (document.hidden) return;
    const today = new Date().toISOString().slice(0, 10);
    if (today !== seenDay) { seenDay = today; seen = new Set(); }
    const r = route();
    document.querySelectorAll(`${CLICKABLE}, [data-testid]`).forEach((el) => {
      if (!isClickable(el) || !isVisible(el)) return;
      const a = actionIdFor(el);
      const k = `${a}|${r}`;
      if (seen.has(k)) return;
      seen.add(k);
      enqueue("seen", a, r);
    });
  };

  const onClick = (e) => {
    const el = resolveClickable(e.target);
    if (!el) return;
    enqueue("click", actionIdFor(el), route());
    clearTimeout(sweepTimer);
    sweepTimer = setTimeout(sweep, 1500);
  };
  const onHide = () => { if (document.hidden) flush(true); };
  const onPageHide = () => flush(true);

  document.addEventListener("click", onClick, true);
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", onPageHide);
  const flushId = setInterval(flush, FLUSH_MS);
  const sweepId = setInterval(sweep, SWEEP_MS);
  sweepTimer = setTimeout(sweep, 2000);

  return () => {
    document.removeEventListener("click", onClick, true);
    document.removeEventListener("visibilitychange", onHide);
    window.removeEventListener("pagehide", onPageHide);
    clearInterval(flushId);
    clearInterval(sweepId);
    clearTimeout(sweepTimer);
    flush(true);
  };
}
